import Typography from '@material-ui/core/Typography';
import { FormattedMessage } from 'react-intl';
import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { REDESIGN_COLORS, REDESIGN_FONTS } from '../../../constants';
import { Backdrop } from '@material-ui/core';
import { GA_EVENT_TREE } from '../../../gaConstants';
import GoogleIcon from '../../../MyComponents/icons/GoogleIcon';
import FacebookIcon from '../../../MyComponents/icons/FacebookIcon';
import MicrosoftIcon from '../../../MyComponents/icons/MicrosoftIcon';
import Hidden from '@material-ui/core/Hidden';
import GoogleClassroomIcon from '../../../MyComponents/icons/GoogleClassroomIcon';
import PhoneQR from '../../../MyComponents/icons/PhoneQR.js';
import userApi from '../../../Api/userApi';
import {
    getGuestTokenForName,
    getStoredGuestNames,
    getValidGuestPayload,
    setGuestToken,
} from '../../../common/guestToken';
import { LIFT_SHADOW, socialSurface } from './loginButtonStyles';

// Shared by the three responsive sizes of the social buttons. The skin comes
// from loginButtonStyles so the subscriptions page renders the same buttons.
const socialButton = {
    ...socialSurface,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
};

const pillButton = {
    backgroundColor: REDESIGN_COLORS.primary,
    borderRadius: '999px',
    boxShadow: '0 4px 12px rgba(0, 124, 134, 0.24)',
    color: REDESIGN_COLORS.white,
    fontFamily: REDESIGN_FONTS.body,
    fontSize: '1rem',
    fontWeight: REDESIGN_FONTS.weightHeading,
    textTransform: 'none',
    letterSpacing: 'normal',
    textAlign: 'center',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
        backgroundColor: REDESIGN_COLORS.primaryHover,
    },
};

// scale() does not affect layout, so the card's max width has to be divided
// by the scale factor or it renders wider than the phone.
const MODAL_SCALE = 1.3;

// Below this height the card drops its zoom flourish and some of its spacing;
// otherwise the pills at the bottom run off a phone in landscape.
const SHORT_VIEWPORT = '@media (max-height: 700px)';
// Only here is there room for the zoomed card: it renders ~490px tall, ~640px
// once scaled, and a transformed box that overflows the top of the backdrop
// cannot be scrolled to.
const ROOMY_VIEWPORT = '@media (min-height: 701px)';

// The card is a normal flex item centred by its auto margins, so it sits in
// the middle of whatever viewport the sign-in opens in (a lesson deep link on
// a tablet included) and never past its edges; if it is still too tall it
// scrolls inside itself. It used to be absolutely positioned and pushed 25vh
// down from the top or 40vh up from the bottom, which ran it off the top of a
// landscape tablet.
const modalCard = {
    position: 'static',
    margin: 'auto',
    boxSizing: 'border-box',
    maxWidth: 'calc(100vw - 32px)',
    maxHeight: 'calc(100vh - 32px)',
    overflowY: 'auto',
    transition: 'transform 0.4s',
    transform: 'scale(1)',
};

const modalCardZoomed = {
    ...modalCard,
    [ROOMY_VIEWPORT]: {
        transform: `scale(${MODAL_SCALE})`,
        maxWidth: `calc((100vw - 32px) / ${MODAL_SCALE})`,
        maxHeight: `calc((100vh - 32px) / ${MODAL_SCALE})`,
    },
};

const useStyles = makeStyles((theme) => ({
    connectText: {
        fontFamily: REDESIGN_FONTS.body,
        fontWeight: REDESIGN_FONTS.weightHeading,
        fontSize: 'clamp(1.35rem, 3vw, 1.75rem)',
        lineHeight: 1.15,
        letterSpacing: '-0.03em',
        textAlign: 'center',
        textTransform: 'none',
        color: REDESIGN_COLORS.white,
        marginBottom: '1.75rem',
        [SHORT_VIEWPORT]: {
            marginBottom: '0.75rem',
        },
    },
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: REDESIGN_COLORS.white,
        backgroundColor: 'rgba(6, 63, 69, 0.88)',
        backdropFilter: 'blur(4px)',
        // Nothing in here is ever allowed to be unreachable.
        overflowY: 'auto',
        alignItems: 'flex-start',
        padding: '16px 0',
        [SHORT_VIEWPORT]: {
            padding: '8px 0',
        },
    },
    // The header and main variants used to fly in from different edges; both
    // now zoom in place, so the card lands in the same spot whichever button
    // or redirect opened it.
    animateInHeader: modalCardZoomed,
    animateOutHeader: modalCard,
    animateInMain: modalCardZoomed,
    animateOutMain: modalCard,
    socialRow: {
        display: 'flex',
        justifyContent: 'center',
        gap: '1.5rem',
        [theme.breakpoints.only('xs')]: {
            gap: '0.75rem',
        },
    },
    socialRowSpaced: {
        marginTop: '2rem',
        [SHORT_VIEWPORT]: {
            marginTop: '0.75rem',
        },
    },
    mobileButton: {
        ...socialButton,
        width: '4rem',
        height: '3.5rem',
        fontSize: '0.5rem',
        lineHeight: '0.7rem',
    },
    tabletButton: {
        ...socialButton,
        width: '5rem',
        height: '4rem',
        fontSize: '0.6rem',
        lineHeight: '0.9rem',
    },
    desktopButton: {
        ...socialButton,
        width: '7rem',
        height: '5rem',
        fontSize: '0.8rem',
        lineHeight: '1rem',
    },
    guestButton: {
        ...pillButton,
        marginTop: '2rem',
        minHeight: '52px',
        padding: '0 30px',
        [SHORT_VIEWPORT]: {
            marginTop: '1rem',
        },
    },
    guestSubmit: {
        ...pillButton,
        gap: '10px',
        width: '100%',
        minHeight: '52px',
        padding: '12px 24px',
        border: 0,
        lineHeight: 1.2,
        '&:disabled': {
            cursor: 'default',
            opacity: 0.6,
        },
    },
    // Secondary to the guest pill: outlined on the dark backdrop, the same
    // treatment the schools card uses on its dark panel.
    linkBoardButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
        marginTop: '1rem',
        [SHORT_VIEWPORT]: {
            marginTop: '0.5rem',
        },
        minHeight: '48px',
        padding: '0 24px',
        border: `2px solid rgba(255, 255, 255, 0.28)`,
        borderRadius: '999px',
        color: REDESIGN_COLORS.white,
        fontFamily: REDESIGN_FONTS.body,
        fontSize: '0.95rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        cursor: 'pointer',
        '&:hover': {
            borderColor: REDESIGN_COLORS.primary,
            background: 'rgba(16, 169, 179, 0.18)',
        },
    },
    linkBoardIcon: {
        width: '1.6rem',
        height: '1.6rem',
        flexShrink: 0,
    },
    // The guest form: the redesign's white card with the same field, chips
    // and pill the static /login page uses, so both guest flows look alike.
    guestForm: {
        boxSizing: 'border-box',
        margin: 'auto',
        width: 'min(26rem, calc(100vw - 32px))',
        padding: 'clamp(22px, 3vw, 32px)',
        border: `1px solid ${REDESIGN_COLORS.sand}`,
        borderRadius: '24px',
        backgroundColor: REDESIGN_COLORS.white,
        boxShadow: LIFT_SHADOW,
        color: REDESIGN_COLORS.body,
        fontFamily: REDESIGN_FONTS.body,
        fontSize: '16px',
        lineHeight: 1.6,
        textAlign: 'left',
        display: 'flex',
        flexDirection: 'column',
        // Landscape phones are ~360-430px tall but 700px+ wide. Stacked, this
        // form has to scroll; side by side it fits outright, using the width
        // that is going spare anyway.
        [SHORT_VIEWPORT]: {
            flexDirection: 'row',
            alignItems: 'stretch',
            gap: '1.25rem',
            padding: '1.25rem',
            width: 'min(46rem, calc(100vw - 32px))',
            '& > *': { flex: '1 1 0', minWidth: 0 },
        },
    },
    guestIntro: {
        display: 'flex',
        flexDirection: 'column',
    },
    guestFields: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
    },
    guestTitle: {
        margin: '0 0 10px',
        fontSize: '1.3rem',
        lineHeight: 1.2,
        letterSpacing: '-0.02em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
    },
    guestInfo: {
        margin: '0 0 14px',
        paddingLeft: '20px',
        color: REDESIGN_COLORS.body,
        fontSize: '0.88rem',
        '& li': {
            margin: '4px 0',
        },
    },
    guestWelcome: {
        margin: '0 0 12px',
        padding: '10px 14px',
        border: `1px solid ${REDESIGN_COLORS.primaryBorderSoft}`,
        borderRadius: '12px',
        background: REDESIGN_COLORS.mist,
        color: REDESIGN_COLORS.dark,
        fontSize: '0.9rem',
        fontWeight: REDESIGN_FONTS.weightStrong,
    },
    guestSavedLabel: {
        display: 'block',
        marginBottom: '6px',
        color: REDESIGN_COLORS.muted,
        fontSize: '0.7rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
    },
    guestChips: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        margin: '0 0 14px',
    },
    guestChip: {
        padding: '6px 12px',
        border: `1px solid ${REDESIGN_COLORS.line}`,
        borderRadius: '999px',
        background: REDESIGN_COLORS.cream,
        color: REDESIGN_COLORS.dark,
        fontFamily: REDESIGN_FONTS.body,
        fontSize: '0.86rem',
        fontWeight: REDESIGN_FONTS.weightStrong,
        cursor: 'pointer',
        '&:hover': {
            borderColor: REDESIGN_COLORS.primary,
            color: REDESIGN_COLORS.link,
        },
    },
    guestField: {
        display: 'grid',
        gap: '6px',
        margin: '6px 0 0',
    },
    guestLabel: {
        color: REDESIGN_COLORS.dark,
        fontSize: '0.86rem',
        fontWeight: REDESIGN_FONTS.weightStrong,
    },
    guestInput: {
        boxSizing: 'border-box',
        width: '100%',
        minHeight: '50px',
        padding: '12px 16px',
        border: `1px solid ${REDESIGN_COLORS.line}`,
        borderRadius: '14px',
        background: REDESIGN_COLORS.cream,
        fontFamily: REDESIGN_FONTS.body,
        fontSize: '1rem',
        fontWeight: 600,
        color: REDESIGN_COLORS.dark,
        outline: 'none',
        transition: 'border-color 150ms ease, box-shadow 150ms ease',
        '&:focus': {
            borderColor: REDESIGN_COLORS.primary,
            boxShadow: '0 0 0 3px rgba(16, 169, 179, 0.15)',
        },
    },
    guestInputInvalid: {
        borderColor: '#F35325',
        '&:focus': {
            borderColor: '#F35325',
            boxShadow: '0 0 0 3px rgba(243, 83, 37, 0.15)',
        },
    },
    guestError: {
        minHeight: '1.2em',
        margin: '6px 0 0',
        color: '#F35325',
        fontSize: '0.84rem',
        fontWeight: REDESIGN_FONTS.weightStrong,
    },
    guestTerms: {
        margin: '10px 0 16px',
        color: REDESIGN_COLORS.muted,
        fontSize: '0.82rem',
        '& a': {
            color: REDESIGN_COLORS.link,
        },
    },
    guestBack: {
        display: 'block',
        width: '100%',
        marginTop: '14px',
        padding: '6px',
        border: 0,
        background: 'none',
        color: REDESIGN_COLORS.link,
        fontFamily: REDESIGN_FONTS.body,
        fontSize: '0.9rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        textAlign: 'center',
        cursor: 'pointer',
        '&:hover': {
            color: REDESIGN_COLORS.primary,
        },
    },
}));

const SocialButtonDesktop = ({ onClick, textId, icon }) => {
    let classes = useStyles();

    return (
        <>
            <Hidden only={['xs', 'sm', 'md']}>
                <div>
                    <div onClick={onClick} className={classes.desktopButton}>
                        <div>{icon}</div>
                        <div style={{ marginTop: '0.5rem' }}>
                            <FormattedMessage id={textId} />
                        </div>
                    </div>
                </div>
            </Hidden>
            <Hidden only={['xs', 'lg', 'xl']}>
                <div>
                    <div onClick={onClick} className={classes.tabletButton}>
                        <div>{icon}</div>
                        <div>
                            <FormattedMessage id={textId} />
                        </div>
                    </div>
                </div>
            </Hidden>
            <Hidden only={['sm', 'md', 'lg', 'xl']}>
                <div>
                    <div onClick={onClick} className={classes.mobileButton}>
                        <div>{icon}</div>
                        <div>
                            <FormattedMessage id={textId} />
                        </div>
                    </div>
                </div>
            </Hidden>
        </>
    );
};

const GuestLoginForm = ({ onBack }) => {
    const classes = useStyles();
    const storedGuest = getValidGuestPayload();
    const savedNames = getStoredGuestNames();
    const [name, setName] = React.useState(storedGuest?.name ?? '');
    const [error, setError] = React.useState(null);
    const [submitting, setSubmitting] = React.useState(false);

    const handleSubmit = (submitName) => {
        if (submitting) return;
        // submitName is only a string when a saved-guest chip is clicked; the
        // form passes the submit event here
        let trimmed = (typeof submitName === 'string' ? submitName : name).trim();
        if (!trimmed) {
            setError('login.guest.nameRequired');
            return;
        }
        setSubmitting(true);
        userApi
            .guestLogin(trimmed, getGuestTokenForName(trimmed))
            .then((res) => {
                setGuestToken(trimmed, res.data.token);
                let existingPath = window.localStorage.getItem('pathBeforeLogin');
                window.localStorage.removeItem('pathBeforeLogin');
                window.location.replace(existingPath || '/');
            })
            .catch((err) => {
                setSubmitting(false);
                setError(err?.response?.status === 400 ? 'login.guest.nameRequired' : 'login.guest.error');
            });
    };

    return (
        <form
            className={classes.guestForm}
            onClick={(e) => e.stopPropagation()}
            onSubmit={(e) => {
                e.preventDefault();
                handleSubmit();
            }}
            noValidate
        >
            <div className={classes.guestIntro}>
                <h2 className={classes.guestTitle}>
                    <FormattedMessage id='login.guest.title' />
                </h2>
                <ul className={classes.guestInfo}>
                    {['login.guest.info1', 'login.guest.info2', 'login.guest.info3'].map((infoId) => (
                        <li key={infoId}>
                            <FormattedMessage id={infoId} />
                        </li>
                    ))}
                </ul>
            </div>
            <div className={classes.guestFields}>
                {storedGuest ? (
                    <p className={classes.guestWelcome}>
                        <FormattedMessage id='login.guest.welcomeBack' values={{ name: storedGuest.name }} />
                    </p>
                ) : null}
                {savedNames.length > 1 ? (
                    <div>
                        <span className={classes.guestSavedLabel}>
                            <FormattedMessage id='login.guest.savedGuests' />
                        </span>
                        <div className={classes.guestChips}>
                            {savedNames.map((savedName) => (
                                <button
                                    key={savedName}
                                    type='button'
                                    className={classes.guestChip}
                                    onClick={() => handleSubmit(savedName)}
                                >
                                    {savedName}
                                </button>
                            ))}
                        </div>
                    </div>
                ) : null}
                <label className={classes.guestField}>
                    <span className={classes.guestLabel}>
                        <FormattedMessage id='login.guest.nameLabel' />
                    </span>
                    <input
                        type='text'
                        autoFocus
                        value={name}
                        maxLength={100}
                        autoComplete='off'
                        aria-invalid={!!error}
                        onChange={(e) => {
                            setName(e.target.value);
                            setError(null);
                        }}
                        className={[classes.guestInput, error ? classes.guestInputInvalid : ''].filter(Boolean).join(' ')}
                    />
                </label>
                <p className={classes.guestError} role='alert'>
                    {error ? <FormattedMessage id={error} /> : null}
                </p>
                <p className={classes.guestTerms}>
                    <FormattedMessage
                        id='login.guest.termsNotice'
                        values={{
                            terms: (
                                <a href='/terms' target='_blank' rel='noopener noreferrer'>
                                    <FormattedMessage id='login.guest.termsLink' />
                                </a>
                            ),
                            policy: (
                                <a href='/privacypolicy' target='_blank' rel='noopener noreferrer'>
                                    <FormattedMessage id='login.guest.policyLink' />
                                </a>
                            ),
                        }}
                    />
                </p>
                <button type='submit' disabled={submitting} className={classes.guestSubmit}>
                    <FormattedMessage id='login.guest.submit' />
                    <span aria-hidden='true'>→</span>
                </button>
                <button type='button' onClick={onBack} className={classes.guestBack}>
                    <FormattedMessage id='login.guest.back' />
                </button>
            </div>
        </form>
    );
};

export const ResponsiveLoginModal = ({ handleLogin, handleClose, open, fromHeader = true }) => {
    const classes = useStyles();
    const [guestMode, setGuestMode] = React.useState(false);

    React.useEffect(() => {
        // always reopen on the main sign-in options, not the guest form
        if (!open) {
            setGuestMode(false);
        }
    }, [open]);

    if (window.isTeams) return null;

    if (open && guestMode) {
        return (
            <Backdrop className={classes.backdrop} open={open} onClick={handleClose}>
                <GuestLoginForm onBack={() => setGuestMode(false)} />
            </Backdrop>
        );
    }

    return (
        <Backdrop className={classes.backdrop} open={open} onClick={handleClose}>
            <div
                className={
                    open
                        ? fromHeader
                            ? classes.animateInHeader
                            : classes.animateInMain
                        : fromHeader
                        ? classes.animateOutHeader
                        : classes.animateOutMain
                }
            >
                <div>
                    <Typography className={classes.connectText}>
                        <FormattedMessage id="redesign.page.home.modal.connect" />
                    </Typography>
                </div>
                <div>
                    <div className={classes.socialRow}>
                        <SocialButtonDesktop
                            onClick={(e) => {
                                window.logAppActivity(GA_EVENT_TREE.mainPage.links.googleLogin);
                                handleLogin('/google');
                                e.preventDefault();
                                e.stopPropagation();
                            }}
                            icon={<GoogleIcon />}
                            textId={'home.loginGoogle'}
                        />
                        <SocialButtonDesktop
                            onClick={(e) => {
                                window.logAppActivity(GA_EVENT_TREE.mainPage.links.googleLogin);
                                handleLogin('/google');
                                e.preventDefault();
                                e.stopPropagation();
                            }}
                            icon={<GoogleClassroomIcon />}
                            textId={'home.loginGoogleClassroom'}
                        />
                    </div>
                    <div className={`${classes.socialRow} ${classes.socialRowSpaced}`}>
                        <SocialButtonDesktop
                            onClick={(e) => {
                                window.logAppActivity(GA_EVENT_TREE.mainPage.links.facebookLogin);
                                handleLogin('/facebook');
                                e.preventDefault();
                                e.stopPropagation();
                            }}
                            icon={<FacebookIcon />}
                            textId={'home.loginFacebook'}
                        />
                        <SocialButtonDesktop
                            onClick={(e) => {
                                window.logAppActivity(GA_EVENT_TREE.mainPage.links.microsoftLogin);
                                handleLogin('/microsoft');
                                e.preventDefault();
                                e.stopPropagation();
                            }}
                            icon={<MicrosoftIcon />}
                            textId={'home.loginMicrosoft'}
                        />
                    </div>
                    <div
                        className={classes.guestButton}
                        onClick={(e) => {
                            setGuestMode(true);
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                    >
                        <FormattedMessage id='login.guest.continueAsGuest' />
                    </div>
                    <div
                        className={classes.linkBoardButton}
                        onClick={(e) => {
                            window.logAppActivity(GA_EVENT_TREE.mainPage.links.linkWhiteboard);
                            handleLogin('linkWhiteboard');
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                    >
                        <PhoneQR className={classes.linkBoardIcon} />
                        <FormattedMessage id='redesign.login.linkWhiteboard' />
                    </div>
                </div>
            </div>
        </Backdrop>
    );
};
